import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

type Props = {
    value?: string; // ISO yyyy-mm-dd
    onChange: (iso: string) => void;
    placeholder?: string;
    className?: string;
    inline?: boolean; // when true, render calendar inline instead of popover
    showRecurrence?: boolean; // when true, show simple recurrence toggles in the calendar
    recurrence?: '' | 'daily' | 'weekly' | 'monthly';
    onRecurrenceChange?: (r: '' | 'daily' | 'weekly' | 'monthly') => void;
    withTime?: boolean; // when true, picking a day advances to a time step before committing
};

function toISO(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function parseISO(iso?: string) {
    if (!iso) return undefined;
    // Accept full ISO with time or date-only yyyy-mm-dd
    if (iso.includes('T')) {
        const d = new Date(iso);
        return isNaN(d.getTime()) ? undefined : d;
    }
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return undefined;
    return new Date(y, m - 1, d);
}

function formatHuman(iso?: string) {
    const d = parseISO(iso);
    if (!d) return '';
    // If time is present, include it in display
    const hasTime = Boolean(iso && iso.includes('T'));
    const dateStr = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    if (!hasTime) return dateStr;
    const timeStr = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${dateStr} • ${timeStr}`;
}

export default function DatePicker({ value, onChange, placeholder = 'Select date', className = '', inline = false, showRecurrence = false, recurrence = '', onRecurrenceChange, withTime = false }: Props) {
    const initial = parseISO(value) || new Date();
    const [open, setOpen] = useState(false);
    const [cursor, setCursor] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));
    const [mode, setMode] = useState<'date' | 'time'>('date');
    const [tempDate, setTempDate] = useState<Date | null>(null); // holds chosen day before time is picked
    const [timeInput, setTimeInput] = useState<string>('09:00');
    const rootRef = useRef<HTMLDivElement | null>(null);

    // Close on outside click or Escape
    useEffect(() => {
        if (!open) return;
        const onMouseDown = (e: MouseEvent) => {
            const el = rootRef.current;
            if (!el) return;
            if (e.target instanceof Node && !el.contains(e.target)) {
                setOpen(false);
                setMode('date');
                setTempDate(null);
            }
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpen(false);
                setMode('date');
                setTempDate(null);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const weeks = useMemo(() => {
        const start = new Date(cursor);
        start.setDate(1);
        const startDay = start.getDay(); // 0-6, Sun-Sat
        const firstGridDate = new Date(start);
        firstGridDate.setDate(1 - startDay); // back to Sunday grid start

        const days: Date[] = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(firstGridDate);
            d.setDate(firstGridDate.getDate() + i);
            days.push(d);
        }
        const out: Date[][] = [];
        for (let i = 0; i < 6; i++) out.push(days.slice(i * 7, i * 7 + 7));
        return out;
    }, [cursor]);

    const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const selected = parseISO(value);
    const now = new Date();

    const CalendarBlock = (
        <div className={`bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-3 ${inline ? '' : 'w-full'}`}>
            <div className="flex items-center justify-between mb-2">
                <button
                    type="button"
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800"
                    onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="text-sm text-gray-200 font-medium">
                    {cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </div>
                <button
                    type="button"
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800"
                    onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
            {showRecurrence && (
                <div className="flex items-center gap-2 mb-2">
                    {([
                        { v: '', label: 'None' },
                        { v: 'daily', label: 'Daily' },
                        { v: 'weekly', label: 'Weekly' },
                        { v: 'monthly', label: 'Monthly' },
                    ] as const).map(opt => (
                        <button
                            key={opt.v}
                            type="button"
                            onClick={() => onRecurrenceChange && onRecurrenceChange(opt.v)}
                            className={`px-2 py-1 rounded-md border text-xs ${recurrence === opt.v ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-800'}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-400 mb-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <div key={d}>{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {weeks.flat().map((d, idx) => {
                    const inMonth = d.getMonth() === cursor.getMonth();
                    const isSel = selected && isSameDay(d, selected);
                    const isToday = isSameDay(d, now);
                    return (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => {
                                if (withTime) {
                                    setTempDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
                                    // Seed time from existing value if it has time
                                    if (value && value.includes('T')) {
                                        try {
                                            const existing = new Date(value);
                                            const hh = String(existing.getHours()).padStart(2, '0');
                                            const mm = String(existing.getMinutes()).padStart(2, '0');
                                            setTimeInput(`${hh}:${mm}`);
                                        } catch { setTimeInput('09:00'); }
                                    } else {
                                        setTimeInput('09:00');
                                    }
                                    setMode('time');
                                } else {
                                    const iso = toISO(d);
                                    onChange(iso);
                                    if (!inline) setOpen(false);
                                }
                            }}
                            className={`h-9 rounded-md border text-sm transition-colors ${isSel
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : inMonth
                                    ? 'bg-gray-900 border-gray-700 text-gray-200 hover:bg-gray-800'
                                    : 'bg-gray-900/60 border-gray-800 text-gray-500'
                                } ${isToday && !isSel ? 'ring-1 ring-gray-600' : ''}`}
                        >
                            {d.getDate()}
                        </button>
                    );
                })}
            </div>
            <div className="flex justify-end mt-2">
                <button
                    type="button"
                    onClick={() => {
                        const iso = toISO(new Date());
                        onChange(iso);
                        if (!inline) setOpen(false);
                    }}
                    className="text-xs text-gray-300 hover:text-white underline"
                >
                    Today
                </button>
            </div>
        </div>
    );

    const TimeBlock = (
        <div className={`bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-3 ${inline ? '' : 'w-full'}`}>
            <div className="flex items-center justify-between mb-2">
                <button
                    type="button"
                    className="px-2 py-1 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800 text-xs"
                    onClick={() => setMode('date')}
                >
                    Back
                </button>
                <div className="text-sm text-gray-200 font-medium truncate">
                    {tempDate ? tempDate.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'Pick a date'}
                </div>
                <div className="w-14" />
            </div>
            <div className="mb-2 text-xs text-gray-400">Pick a time</div>
            <input
                type="time"
                value={timeInput}
                onChange={(e) => {
                    const v = e.target.value;
                    setTimeInput(v);
                    // Commit immediately on change for a simpler experience
                    if (!tempDate) return;
                    const [hh, mm] = (v || '09:00').split(':').map(Number);
                    const out = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate(), hh || 9, mm || 0, 0, 0);
                    onChange(out.toISOString());
                    if (!inline) setOpen(false);
                    setMode('date');
                }}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200"
            />
        </div>
    );

    return (
        <div className={`relative ${className}`} ref={rootRef}>
            {inline ? (
                CalendarBlock
            ) : (
                <>
                    <button
                        type="button"
                        onClick={() => { setOpen(v => !v); setMode('date'); }}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-left text-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-600 flex items-center gap-2"
                    >
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className={value ? 'text-gray-200' : 'text-gray-400'}>
                            {value ? formatHuman(value) : placeholder}
                        </span>
                    </button>
                    {open && (
                        <div className="mt-2 w-full">
                            {mode === 'date' ? CalendarBlock : TimeBlock}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
